import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	GlobalShortcutsMenu,
	type GlobalShortcutsItem,
	type GlobalShortcutsMenuProps,
	type ViewAllAppsConfig,
} from '@atlassian/global-shortcuts';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	GroupDropIndicatorWithDragOver,
	isL1ItemData,
	useDragState,
} from '@atlassian/navigation-drag-and-drop';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useInViewport } from '../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useItemsAndShortcuts } from '../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../controllers/options-provider';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';
import { useGlobalShortcutsTransformation } from '../../controllers/transformation/use-global-shortcuts-transformation';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import MoreMenu from '../more-menu';
import { INTERNAL_GLOBAL_ITEMS } from '../more-menu/constants';
import { HideFromSidebarButton } from '../more-menu/hide-from-sidebar-button';
import { ShowInSidebarButton } from '../more-menu/show-in-sidebar-button';
import { messages } from '../side-nav/messages';

export function PostMainItems({
	postItemsElem,
	postMainElem,
	draggingL1Group = null,
	openShortcutsInSameTab,
	viewAllAppsConfig,
	ShortcutsSlot,
}: PostMainItemsProps): React.JSX.Element {
	const { appLabel, disableMenuCustomization, intlMode, l1DragAndDropEnabled } = useOptions();
	const { formatMessage } = useIntl();

	const { allItems, growthItemShortcuts, shortcuts } = useItemsAndShortcuts();
	const peekMenuId = usePeekMenuId();

	const { hiddenItemMenuIds, hiddenShortcutMenuIds, visibleShortcutMenuIds, hiddenMenuIds } =
		useUserMenuDataShapes();

	const { fireClickedSideNavItemEvent } = useNavAnalytics();

	const { shortcutsWithMenuId } = useGlobalShortcutsTransformation({
		shortcutsConfig: shortcuts,
		visibleShortcuts: visibleShortcutMenuIds,
		growthShortcutsConfig: growthItemShortcuts,
	});

	const { groupDropIndicatorRef, dragSource } = useDragState({
		dragAndDropEnabled: l1DragAndDropEnabled,
		isItemData: isL1ItemData,
	});

	const handleClickMoreMenuItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.more, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const handleClickCustomizeItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.customize, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const peekItem = !!peekMenuId && hiddenItemMenuIds.includes(peekMenuId) && allItems[peekMenuId];
	const isDragSourceHidden = dragSource && hiddenMenuIds.includes(dragSource);

	const canMenuItemBeHidden = useMemo(() => {
		return !!(draggingL1Group && dragSource && allItems[dragSource]?.isHideable !== false);
	}, [allItems, dragSource, draggingL1Group]);

	const moreMenu = (
		<MoreMenu
			appLabel={appLabel}
			intlMode={intlMode}
			items={allItems}
			itemIds={hiddenItemMenuIds}
			onClickCustomizeSidebarMenuItem={handleClickCustomizeItem}
			onClickMoreMenuTrigger={handleClickMoreMenuItem}
			openShortcutsInSameTab={openShortcutsInSameTab}
			shortcutItemIds={hiddenShortcutMenuIds}
			shortcutItems={shortcuts}
		/>
	);

	const globalAppShortcuts = (
		<GlobalAppShortcuts
			shortcutItems={shortcutsWithMenuId}
			openShortcutsInSameTab={openShortcutsInSameTab}
			viewAllAppsConfig={viewAllAppsConfig}
			ShortcutsSlot={ShortcutsSlot}
		/>
	);

	return (
		<Stack space="space.100">
			{postItemsElem}
			{!!shortcutsWithMenuId.length &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={draggingL1Group === L1_MENU_GROUPS.SHORTCUTS}
						ariaLabel={formatMessage(messages.shortcutsSectionScreenReader, { item: dragSource })}
					>
						{globalAppShortcuts}
					</GroupDropIndicatorWithDragOver>
				) : (
					globalAppShortcuts
				))}
			{peekItem && (
				<MenuList testId={PEEK_TEST_ID}>
					<NavItem key={peekMenuId} id={peekMenuId} item={peekItem} isInMoreMenu peek />
				</MenuList>
			)}
			{!disableMenuCustomization &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={canMenuItemBeHidden}
						ariaLabel={
							isDragSourceHidden
								? formatMessage(messages.showInSidebarSectionScreenReader, { item: dragSource })
								: formatMessage(messages.hideFromSidebarSectionScreenReader, { item: dragSource })
						}
					>
						<Box xcss={cx(canMenuItemBeHidden ? styles.hidden : styles.visible)}>{moreMenu}</Box>
						<Box xcss={cx(canMenuItemBeHidden ? styles.visible : styles.hidden)}>
							<MenuList>
								{isDragSourceHidden ? <ShowInSidebarButton /> : <HideFromSidebarButton />}
							</MenuList>
						</Box>
					</GroupDropIndicatorWithDragOver>
				) : (
					moreMenu
				))}
			{postMainElem}
		</Stack>
	);
}

export interface PostMainItemsProps extends Pick<
	GlobalSideNavigationProps,
	| 'postItemsElem'
	| 'postMainElem'
	| 'openShortcutsInSameTab'
	| 'ShortcutsSlot'
	| 'viewAllAppsConfig'
> {
	draggingL1Group?: string | null;
}

const styles = cssMap({
	visible: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

function GlobalAppShortcuts({
	openShortcutsInSameTab,
	shortcutItems,
	viewAllAppsConfig,
	ShortcutsSlot,
}: {
	openShortcutsInSameTab?: boolean;
	shortcutItems: GlobalShortcutsItem[];
	ShortcutsSlot?: GlobalSideNavigationProps['ShortcutsSlot'];
	viewAllAppsConfig?: ViewAllAppsConfig;
}) {
	const { app, intlMode, l1DragAndDropEnabled } = useOptions();
	const { fireViewedShortcutsEvent } = useNavAnalytics();

	const containerRef = useRef<HTMLDivElement | null>(null);
	const fireAnalytics = useCallback(
		() => fireViewedShortcutsEvent(shortcutItems),
		[fireViewedShortcutsEvent, shortcutItems],
	);

	useInViewport({ ref: containerRef, onInViewport: fireAnalytics });

	// A pre-bound shortcuts menu handed to the `ShortcutsSlot` render prop. The
	// host renders this wherever it wants; any props it passes override the
	// GSN-internal defaults. Memoised to keep a stable component identity so the
	// slot subtree is not remounted on every render (see the drag-and-drop
	// rationale in the sibling comment in `PostMainItems`).
	const ShortcutsMenuComponent = useCallback(
		function BoundGlobalShortcutsMenu(
			overrides: Partial<Pick<GlobalShortcutsMenuProps, 'shortcutItems' | 'testId'>>,
		) {
			return (
				<GlobalShortcutsMenu
					shortcutItems={shortcutItems}
					testId={SHORTCUTS_TEST_ID}
					{...overrides}
					enableDragAndDrop={l1DragAndDropEnabled}
					intlMode={intlMode}
					openInSameTab={openShortcutsInSameTab}
					product={app}
					viewAllAppsConfig={viewAllAppsConfig}
				/>
			);
		},
		[app, intlMode, l1DragAndDropEnabled, openShortcutsInSameTab, shortcutItems, viewAllAppsConfig],
	);

	// Inline the gate (rather than calling `componentWithFG` inside the render
	// body) so the rendered tree's component identity is stable across
	// renders. See sibling comment in `PostMainItems` for the rationale.
	const shortcutsMenu = ShortcutsSlot ? (
		<ShortcutsSlot Component={ShortcutsMenuComponent} shortcutItems={shortcutItems} />
	) : (
		<ShortcutsMenuComponent />
	);

	return <Box ref={containerRef}>{shortcutsMenu}</Box>;
}
